import { MessagePayload, getToken, onMessage } from 'firebase/messaging';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROLES } from '../../consts/language';
import CustomDialog from '../../components/Dialog/Dialog';
import { VAPID_KEY_PROD, VAPID_KEY_STAG } from '../../configs/config';
import db_config from '../../configs/db_config';
import usePermission from '../../hooks/usePermission';
import { addNativeMessageListener, getNativeToken, isNative, removeNativeListener, requestNativePermission } from '../../libs/capacitor-messaging';
import { db, messaging } from '../../libs/firebase';
import { doc, getDoc, updateDoc } from '../../libs/firestore-wrapper';
import { ServiceRequestSoundContext } from '../AudioNotification/ServiceRequestSound';
import { useAuthContext } from '../AuthContext/AuthContext';
import { Message } from '../OrderContext';

interface NewOrderAlertsContextValue {
  showNewOrderAlert: (id: string) => void;
  isNotificationEnabled: boolean;
  notifications: Message[];
  markAsRead: () => void;
  setNotifications: React.Dispatch<React.SetStateAction<Message[]>>;
  deviceToken: string;
}

export const NewOrderAlertsContext = createContext<NewOrderAlertsContextValue>({
  showNewOrderAlert: () => {},
  isNotificationEnabled: false,
  markAsRead: () => {},
  notifications: [],
  setNotifications: () => {},
  deviceToken: '',
});

export const NewOrderAlertsContextProvider = ({ children }: { children: ReactNode }) => {
  const [id, setId] = useState<string>('');
  const { user, userInfo } = useAuthContext();
  const { restaurantId: permissionRestaurantId } = usePermission();
  const ServiceRequestSound = useContext(ServiceRequestSoundContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [open_alert_dialog, setOpenAlertDialog] = useState(false);
  const [deviceToken, setDeviceToken] = useState<string>('');
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);
  const [notifications, setNotifications] = useState<Message[]>([]);

  const isSuperAdmin = userInfo?.role === ROLES.SUPER_ADMIN;
  const adminRestaurantId = userInfo?.restaurants?.find((restaurant: any) => restaurant.is_admin_store)?.id;
  const restaurantId = isSuperAdmin ? adminRestaurantId : permissionRestaurantId;

  const showNewOrderAlert = (id: string) => {
    setOpenAlertDialog(true);
    ServiceRequestSound.handlePlaySound();
    setId(id);
  };

  const handleCloseAlert = () => {
    ServiceRequestSound.handleStopSound();
    setOpenAlertDialog(false);
  };

  const handleRedirect = () => {
    handleCloseAlert();
    const role = userInfo?.role;
    if (role === ROLES.SUPER_ADMIN) {
      navigate(`/astrolab-store/${restaurantId}/dashboard?id=${id}`);
    } else {
      navigate(`${location.pathname}?id=${id}`);
    }
  };

  const getMessagingToken = async () => {
    if (!messaging) {
      setIsNotificationEnabled(false);
      return;
    }
    try {
      let permission = 'default';

      if (isNative()) {
        const result = await requestNativePermission();
        permission = result.granted ? 'granted' : 'denied';
      } else {
        permission = await Notification.requestPermission();
      }

      if (permission === 'granted' && user?.uid) {
        const userDocRef = doc(db, db_config.DB_TABLE_RESTAURANT_CONFIG, restaurantId);
        const userDoc = await getDoc(userDocRef);
        const deviceTokens = userDoc.data()?.device_tokens || [];
        let token = deviceToken;
        const vapid_key = process.env.REACT_APP_ENV === 'staging' ? VAPID_KEY_STAG : VAPID_KEY_PROD;

        if (!token || token === '') {
          if (isNative()) {
            try {
              token = await getNativeToken();
            } catch (e) {
              console.warn('Capacitor messaging not available', e);
              token = '';
            }
          } else {
            token = await getToken(messaging, { vapidKey: vapid_key });
          }
        }
        setDeviceToken(token);

        if (!deviceTokens?.includes(token) && user?.uid) {
          await updateDoc(userDocRef, { device_tokens: [...deviceTokens, token] });
        }
        setIsNotificationEnabled(true);
      } else if (permission === 'denied') {
        setIsNotificationEnabled(false);
      }
    } catch (error) {
      setIsNotificationEnabled(false);
    }
  };

  useEffect(() => {
    if (messaging && user?.uid && userInfo && restaurantId) {
      getMessagingToken();
    }
  }, [user?.uid, userInfo, restaurantId]);

  useEffect(() => {
    if (!messaging) return;
    let removeListener: any = null;
    if (isNative()) {
      (async () => {
        try {
          removeListener = await addNativeMessageListener((msg: any) => {
            const orderId = msg?.data?.id || msg?.data?.id?.toString();
            if (orderId) {
              showNewOrderAlert(orderId);
              setNotifications((prev) => [{ isUnread: true, id: orderId }, ...prev]);
            }
          });
        } catch (e) {
          console.warn('Capacitor messaging listener failed', e);
        }
      })();
    } else {
      const unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
        const orderId = payload.data?.id;
        if (orderId) {
          showNewOrderAlert(orderId);
          setNotifications((prev) => [{ isUnread: true, id: orderId }, ...prev]);
        }
      });
      removeListener = unsubscribe;
    }

    return () => {
      try {
        if (isNative()) {
          removeNativeListener(removeListener);
        } else {
          if (removeListener && typeof removeListener === 'function') removeListener();
        }
      } catch (e) {
        // ignore
      }
    };
  }, []);

  const markAsRead = () => {
    setNotifications((prev) => prev.map((i) => ({ ...i, isUnread: false })));
  };

  return (
    <>
      <NewOrderAlertsContext.Provider value={{ showNewOrderAlert, isNotificationEnabled, markAsRead, notifications, setNotifications, deviceToken }}>
        <CustomDialog open={open_alert_dialog} onClose={handleCloseAlert} id={id} onRedirect={handleRedirect} />
        {children}
      </NewOrderAlertsContext.Provider>
    </>
  );
};
